import React,{useState, useEffect} from 'react';
import styles from '../../styles/components/sections/FeaturesSection.module.css';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';
import { sanitizeHtml } from '../../utils/helpers/sanitize';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { OptimizedImage } from '../common';
import { scrollToBookingForm } from '../../utils/helpers/smoothScroll';

/**
 * Features Section Component
 * Displays a centered section title/description with feature cards
 *
 * @param {Object} props
 * @param {Object} props.data - The features data
 * @param {String} props.data.Title - Main section title
 * @param {String} props.data.Description - Main section description
 * @param {Object} props.data.Button - Button object with name, url, isHidden, LinkStyle
 * @param {Array} props.data.Features - Array of feature objects
 * @param {String} props.className - Optional additional CSS class
 */

const FeaturesSection = ({ data, className = '' }) => {
    // const { isDarkMode } = useDarkMode();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isClient, setIsClient] = useState(false);
  
  
  
    // Set client-side flag
    useEffect(() => {
        setIsClient(true);
    }, []);
  
    // Detect system dark mode preference
    useEffect(() => {
        if (!isClient) return;
  
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(mediaQuery.matches);
  
        const handleChange = e => {
            setIsDarkMode(e.matches);
            updateTheme(e.matches ? 'dark' : 'light');
        };
  
        mediaQuery.addEventListener('change', handleChange);
  
        // Check for saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDarkMode(savedTheme === 'dark');
            updateTheme(savedTheme);
        } else {
            // Set initial theme based on system preference
            updateTheme(mediaQuery.matches ? 'dark' : 'light');
        }
  
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [isClient]);
  
    // Detect mobile device
    useEffect(() => {
        if (!isClient) return;
  
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
  
        checkMobile();
        window.addEventListener('resize', checkMobile);
  
        return () => window.removeEventListener('resize', checkMobile);
    }, [isClient]);
  
    // Update theme on document
    const updateTheme = theme => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    };
    if (!data) return null;

    // Helper function to create HTML from the rich text content
    const createMarkup = htmlContent => {
        const sanitizedContent = sanitizeHtml(htmlContent);
        return { __html: sanitizedContent };
    };

    const features = data.Features || [];

    const handleBook = () => scrollToBookingForm(100)

    return (
        <section className={`${styles['features-section']} ${className}`}>
            <div className="tw-container">
                <div className={styles['features-container']}>
                    {/* Left Section - Header and Button */}
                    <div className={styles['features-header']}>
                        <p className={`${styles['features-heading']} ${isDarkMode ? styles['features-heading-dark'] : ''}`}>
                            {data.Title}
                        </p>
                        {data.Description && (
                            <div className={`${styles['features-description']} ${isDarkMode ? styles['features-description-dark'] : ''}`}>
                                {data.Description}
                            </div>
                        )}
                        {/* Button */}
                        {data.Button && !data.Button.isHidden && (
                            <button
                                onClick={handleBook}
                                style={{
                                    filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none',
                                }}
                                className={`${styles['features-button']} ${isDarkMode ? styles['features-button-dark'] : ''}`}
                            >
                                {data.Button.name}
                            </button>
                        )}
                    </div>

                    {/* Right Section - Features Grid */}
                    <div className={styles['features-grid']}>
                        {features.map((feature, index) => (
                            <div key={feature.id || index} className={`${styles['feature-card']} ${isDarkMode ? styles['feature-dark-card'] : ''}`}>
                                {/* Feature Icon */}
                                {feature.Icon?.url && (
                                    <div className={`${styles['feature-icon-container']} ${isDarkMode ? styles['feature-icon-container-dark'] : ''}`}>
                                        <OptimizedImage
                                            src={replaceImageUrl(feature.Icon.formats?.small?.url || feature.Icon.url)}
                                            alt={feature.Icon.alternativeText || feature.Name || 'Feature icon'}
                                            width={100}
                                            height={100}
                                            priority={false}
                                            loading="lazy"
                                            quality={65}
                                            sizes="100px"
                                            className={`${styles['feature-icon']} ${isDarkMode ? styles['feature-icon-dark'] : ''}`}
                                        />
                                    </div>
                                )}

                                {/* Feature Content */}
                                <div className={styles['feature-content']}>
                                    <p className={`${styles['feature-title']} ${isDarkMode ? styles['feature-title-dark'] : ''}`}>
                                        {feature.Name}
                                    </p>
                                    {feature.Description && (
                                        <div 
                                            className={`${styles['feature-description']} ${isDarkMode ? styles['feature-description-dark'] : ''}`}
                                            dangerouslySetInnerHTML={createMarkup(feature.Description)}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturesSection;
